import { FormField, ValueFormField, ViewModel } from '@sitecore-jss/sitecore-jss-forms';
import { FieldChangeCallback } from '@sitecore-jss/sitecore-jss-react-forms';
import { EventType, FormEvent } from 'lib/enum';
import { LoadCountiesByZipCode, LoadEventOccurrenceEvolveId, LoadEventTitleById } from './FormEventHandlers';
import { SitecoreContextValue } from '@sitecore-jss/sitecore-jss-nextjs';

export * from './FormFieldAriaProps';

export const handleEvent = (
  postMessageEvent: MessageEvent,
  currentFieldName: string,
  expectedEventType: string,
  callback?: (fieldValue: unknown) => void
) => {
  const { data, event } = postMessageEvent.data;
  const isConditionalRequiredEvent = event === FormEvent.SetConditionalRequiredFlag;
  const ignoreEvent = !!event && !isConditionalRequiredEvent;

  // Do not process if data is not found or its a named event which is not relevant to field value setting
  if (!data || ignoreEvent) {
    return false;
  }

  // console.log('Received post message event:', data, event, currentFieldName);
  const { fieldName, eventType, fieldValue, targetFieldName } = data;
  const fieldNameToCompare = isConditionalRequiredEvent ? targetFieldName : fieldName;
  const isEventForCurrentField = fieldNameToCompare && fieldNameToCompare.split(',').includes(currentFieldName); // Support multiple field names in event by splitting with comma and checking if current field is included
  const isExpectedEventType = eventType && eventType.split(',').includes(expectedEventType); // Support multiple event types in event by splitting with comma and checking if expected event type is included

  // Only process if the fieldName matches with current field and event type is same as expected event type
  if (isEventForCurrentField && eventType && isExpectedEventType) {
    // This event is for conditional fields - for all target fields set the value according to the flag passed
    if (isConditionalRequiredEvent) {
      const $hiddenField = document.getElementById(`hidden_${currentFieldName}_conditional_required`);

      if ($hiddenField) {
        let $hiddenFieldValue = $hiddenField.getAttribute('data-value') || '';

        if (fieldValue === 'checked') {
          $hiddenFieldValue = `${$hiddenFieldValue}|${fieldName}`;
        } else {
          $hiddenFieldValue = $hiddenFieldValue?.replace(`|${fieldName}`, '') || '';
        }
        // console.log($hiddenField.id, $hiddenFieldValue);
        $hiddenField.setAttribute('data-value', $hiddenFieldValue);
      }
    }

    callback && callback(fieldValue);
    return true;
  }

  return false;
};

export const handleLoadValuesEvent = (
  postMessageEvent: MessageEvent,
  currentFieldName: string,
  callback?: (fieldValue: unknown) => void
) => handleEvent(postMessageEvent, currentFieldName, EventType.LOAD_VALUES, callback);

export const handleSetValueEvent = (
  postMessageEvent: MessageEvent,
  currentFieldName: string,
  callback?: (fieldValue: unknown) => void
) => handleEvent(postMessageEvent, currentFieldName, EventType.SET_VALUE, callback);

export const handleErrorEvent = (
  postMessageEvent: MessageEvent,
  currentFieldName: string,
  callback?: (fieldValue: unknown) => void
) => handleEvent(postMessageEvent, currentFieldName, EventType.ERROR, callback);

export const handleConditionalRequiredEvent = (
  postMessageEvent: MessageEvent,
  currentFieldName: string,
  callback?: (fieldValue: unknown) => void
) => handleEvent(postMessageEvent, currentFieldName, EventType.SET_CONDITIONAL_REQUIRED, callback);

/**
 * @param {ValueFormField} field
 * @param {string} fieldValue
 * @param {FieldChangeCallback} callback
 */
export const handleOnChangeWithEvent = (
  field: FormField<ViewModel>,
  fieldValue: string,
  onChangeCallback?: FieldChangeCallback,
  setFieldValueStateCallback?: (fieldValue: string, triggerEvent?: boolean) => void,
  eventType: string = EventType.FIELD_CHANGED
) => {
  let valid = true;
  const errorMessages = [];

  // custom client validation logic here
  if (field.model.required && !fieldValue) {
    valid = false;
    errorMessages.push(`${field.model.title} is required`);
  }

  const onChangeEventMessage = field.model?.onChangeEventMessage || '';
  const targetFieldName = field.model?.targetFieldName || '';
  const fieldName = field.model.name;

  if (onChangeEventMessage) {
    const $eventData = {
      event: onChangeEventMessage,
      data: {
        fieldName: fieldName,
        targetFieldName: targetFieldName,
        eventType: eventType,
        fieldValue: fieldValue,
      },
    };

    console.log('handleOnChangeWithEvent', field, $eventData);
    window.postMessage($eventData, '*');
  }

  // Set State on the custom field component (if needed)
  setFieldValueStateCallback && setFieldValueStateCallback(fieldValue, false);

  const { valueField } = field;
  const { name } = valueField as ValueFormField;

  // Trigger the onChange callback to update form state and perform validation
  onChangeCallback && onChangeCallback(`${name}` || field.model.name, fieldValue, valid, errorMessages);
};

export const handleFormMessageEvents = async (
  postMessageEvent: MessageEvent,
  sitecoreContext: SitecoreContextValue
): Promise<void> => {
  const { data, event } = postMessageEvent.data;

  // Do not process if data is not found or its a named event which is not relevant to field value setting
  if (!data || !event || !sitecoreContext) {
    return;
  }

  const { fieldName, eventType, fieldValue, targetFieldName } = data;
  console.log('handleFormMessageEvents:', event, fieldName, eventType, fieldValue, targetFieldName);

  switch (event) {
    case FormEvent.LoadCountiesByZipCode:
      await LoadCountiesByZipCode(fieldValue, fieldName, targetFieldName || '');
      break;
    case FormEvent.LoadEventTitleById:
      await LoadEventTitleById(fieldValue, targetFieldName || '', sitecoreContext);
      break;
    case FormEvent.LoadEventOccurrenceEvolveId:
      await LoadEventOccurrenceEvolveId(fieldValue, targetFieldName || '', sitecoreContext);
      break;
    default:
      break;
  }
};
